import { useState, useEffect } from "react";
import { validatePassword } from "../utils/validation";
import { sanitizePhone, sanitizeUsbcId } from "../utils/sanitization";

/**
 * Hook to manage profile form state and validation
 * @param {Object} profile - Current user profile
 * @returns {Object} Form state and handlers
 */
export const useProfileForm = (profile) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    usbcId: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sync form data with profile
  useEffect(() => {
    if (profile) {
      setFormData((prev) => ({
        ...prev,
        fullName: profile.full_name || "",
        phone: profile.phone_number || "",
        usbcId: profile.usbcId || "",
      }));
    }
  }, [profile]);

  /**
   * Handle form field changes with sanitization
   * @param {Event} e - Change event
   * @param {Function} setErrors - Function to set validation errors
   */
  const handleChange = (e, setErrors) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    if (name === "phone") {
      sanitizedValue = sanitizePhone(value);
    } else if (name === "usbcId") {
      // Only allow digits, limit to 15 digits
      sanitizedValue = sanitizeUsbcId(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    // Clear errors when user starts typing
    if (setErrors) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[name]) {
          delete newErrors[name];
        }
        return newErrors;
      });
    }

    // Handle password validation
    if (name === "newPassword") {
      const validation = validatePassword(value);
      if (validation) {
        setPasswordChecks(validation.checks);
      } else {
        setPasswordChecks({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
        });
      }
    }

    // Handle password match validation
    if (name === "confirmPassword" || name === "newPassword") {
      const newPassword = name === "newPassword" ? value : formData.newPassword;
      const confirmPassword =
        name === "confirmPassword" ? value : formData.confirmPassword;

      if (setErrors) {
        if (confirmPassword && newPassword !== confirmPassword) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match",
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors.confirmPassword) {
              delete newErrors.confirmPassword;
            }
            return newErrors;
          });
        }
      }
    }
  };

  /**
   * Reset password fields
   */
  const resetPasswordFields = () => {
    setFormData((prev) => ({
      ...prev,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    }));
  };

  /**
   * Reset a specific field to profile value
   * @param {string} field - Field name
   */
  const resetField = (field) => {
    if (!profile) return;

    setFormData((prev) => ({
      ...prev,
      [field]:
        field === "fullName"
          ? profile.full_name || ""
          : field === "phone"
          ? profile.phone_number || ""
          : field === "usbcId"
          ? profile.usbcId || ""
          : prev[field],
    }));
  };

  return {
    // State
    formData,
    passwordChecks,
    showPassword,
    showConfirmPassword,
    // Actions
    setFormData,
    handleChange,
    resetPasswordFields,
    resetField,
    setShowPassword,
    setShowConfirmPassword,
  };
};

